import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { DialogService } from "@obg/mat.ui.core";
import {
	CommonState,
	MULTI_SESSION_LIMIT_MESSAGE_CONTEXT,
	MultiSessionLimit,
	MultiSessionLimitAction,
	MultiSessionLimitResponse,
	MultiSessionLimits,
	MultiSessionLimitSelector,
} from "@obg/ngx.common";
import { AbstractTranslatableSnackBarService, LimitsSummary, MessageService, MessageType } from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { multiSessionLimitAmountValidator } from "@obg/ngx.ui.common";
import { pickBy } from "lodash-es";
import { merge, Observable, Subscription } from "rxjs";
import { take, tap } from "rxjs/operators";

import { ResponsibleGamingFormService } from "../../containers/responsible-gaming-form.service";

import { MULTI_SESSION_LIMIT_DIALOG } from "./multi-session-limit-confirmation-dialog/multi-session-limit-confirmation.dialog";
import { MultiSessionLimitConfirmationModel } from "./multi-session-limit-confirmation-dialog/multi-session-limit-confirmation.model";
import {
	MultiSessionLimitFormGroup,
	MultiSessionLimitPeriod,
	MultiSessionLimitPeriodTranslationsEnum,
	MultiSessionLimitTemplate,
	multiSessionLimitPeriodToSuggestionAmount,
	multiSessionLimitPeriodToPeriodTranslation,
} from "./multi-session-limit.container.model";

@Component({
	selector: "obg-m-multi-session-limit-container",
	templateUrl: "./multi-session-limit.container.html",
	styleUrls: ["./multi-session-limit.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class MultiSessionLimitContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-multi-session-limit") hostClass = true;

	readonly MULTI_SESSION_LIMIT_ALERT_ID = "MULTI_SESSION_LIMIT_ALERT_ID";
	readonly MULTI_SESSION_LIMIT_REMOVE_ALERT_ID = "MULTI_SESSION_LIMIT_REMOVE_ALERT_ID";
	readonly TRANSLATION_PREFIX = "multi-session-limit";

	readonly SESSION_LIMIT_PERIOD_CONTROLS = MultiSessionLimitPeriod;

	isSettingBusy = false;
	isRemovingBusy = false;

	currentSessionLimit: MultiSessionLimits | undefined;
	currentSessionLimitConverted: MultiSessionLimitTemplate | undefined;
	upcomingSessionLimit: MultiSessionLimitTemplate | undefined;
	sessionLimits$: Observable<LimitsSummary | undefined> | undefined;

	formGroup: MultiSessionLimitFormGroup;
	isSessionLimitEdited = false;
	placeholder = "24";

	private data$$: Subscription | undefined;
	private isFormDirty$$: Subscription | undefined;
	private submitDialogSubscription$$: Subscription | undefined;

	constructor(
		private multiSessionLimitSelector: MultiSessionLimitSelector,
		private multiSessionLimitAction: MultiSessionLimitAction,
		private formBuilder: UntypedFormBuilder,
		private store: Store<CommonState>,
		private snackBarService: AbstractTranslatableSnackBarService,
		private changeDetector: ChangeDetectorRef,
		private messageService: MessageService,
		private responsibleGamingFormService: ResponsibleGamingFormService,
		private dialogService: DialogService
	) {
		this.formGroup = this.initFormGroup();
	}

	ngOnInit() {
		this.store.dispatch(this.multiSessionLimitAction.get());

		const sessionLimits$ = this.store.pipe(
			select(this.multiSessionLimitSelector.get()),
			tap((sessionLimit) => {
				this.currentSessionLimit = sessionLimit;
				this.currentSessionLimitConverted = undefined;
				this.upcomingSessionLimit = undefined;
				if (sessionLimit?.current) {
					this.currentSessionLimitConverted = this.mapSessionLimits(sessionLimit.current);
				}

				if (sessionLimit?.upcoming) {
					this.upcomingSessionLimit = this.mapSessionLimits(sessionLimit.upcoming);
				}
			})
		);

		const sessionLimitUpdate$ = this.messageService
			.consume$({
				context: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.update,
			})
			.pipe(
				tap((message) => {
					this.isSettingBusy = false;
					this.formGroup.setValue({
						sessionLimitAmount: "",
						sessionLimitPeriod: MultiSessionLimitPeriod.Daily,
					});
					if (message[0].type === MessageType.Success) {
						this.updateSuccess("update_success");
						return;
					}
					this.updateFail();
				})
			);

		const sessionLimitPeriodControlChanges$ = this.formGroup.controls.sessionLimitPeriod.valueChanges.pipe(
			tap((value: MultiSessionLimitPeriod) => {
				this.formGroup.controls.sessionLimitAmount.reset();
				this.placeholder = multiSessionLimitPeriodToSuggestionAmount[value].toString();
			})
		);

		const isBusy$ = this.store.pipe(
			select(this.multiSessionLimitSelector.isBusy()),
			tap((isBusy) => (this.isSettingBusy = isBusy))
		);

		const resetSessionLimitAmountOnOpeningAccordion$ = this.responsibleGamingFormService.resetForm$.pipe(
			tap(() => {
				this.formGroup.controls.sessionLimitAmount.reset();
			})
		);

		this.data$$ = merge(
			sessionLimits$,
			sessionLimitUpdate$,
			sessionLimitPeriodControlChanges$,
			isBusy$,
			resetSessionLimitAmountOnOpeningAccordion$
		).subscribe(() => this.changeDetector.markForCheck());

		this.isFormDirty$$ = this.formGroup.valueChanges.subscribe(() =>
			this.responsibleGamingFormService.sendIsFormDirty(this.formGroup.dirty)
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.isFormDirty$$?.unsubscribe();
		this.submitDialogSubscription$$?.unsubscribe();
	}

	editSessionLimit(): void {
		this.isSessionLimitEdited = true;
	}

	submitNewLimit(): void {
		const newLimitPayload: MultiSessionLimitConfirmationModel = {
			upcoming: this.getMultiSessionLimitValues(
				this.formGroup.controls.sessionLimitAmount.value,
				this.formGroup.controls.sessionLimitPeriod.value as MultiSessionLimitPeriod
			),
			current: this.currentSessionLimit?.current,
			hideFooter: true,
			hideClose: true,
		};
		this.submitSessionLimit(newLimitPayload);
	}

	submitSessionLimit(sessionLimitPayload: MultiSessionLimitConfirmationModel): void {
		this.submitDialogSubscription$$ = this.dialogService
			.openComponent(MULTI_SESSION_LIMIT_DIALOG, {
				width: "480px",
				data: sessionLimitPayload,
			})
			.afterClosed()
			.subscribe((isConfirmed: boolean) => {
				if (!isConfirmed) {
					return;
				}

				this.store.dispatch(this.multiSessionLimitAction.update(sessionLimitPayload.upcoming));
				this.isSettingBusy = true;
				this.changeDetector.markForCheck();
			});
	}

	private initFormGroup(): MultiSessionLimitFormGroup {
		return this.formBuilder.group({
			sessionLimitAmount: this.formBuilder.control("", [multiSessionLimitAmountValidator]),
			sessionLimitPeriod: this.formBuilder.control(MultiSessionLimitPeriod.Daily, [Validators.required]),
		}) as MultiSessionLimitFormGroup;
	}

	private updateFail(): void {
		const snackbar = this.snackBarService
			.openSnackBar$(
				{
					message: "general.e",
					duration: 4000,
					action: "general.close",
					type: MessageType.Error,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			.subscribe();
		snackbar.unsubscribe();
	}

	private updateSuccess(suffix: string): void {
		const snackbar = this.snackBarService
			.openSnackBar$(
				{
					message: `${this.TRANSLATION_PREFIX}.m_multi_session_limit_${suffix}`,
					duration: 4000,
					action: "general.close",
					type: MessageType.Success,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			.subscribe();
		snackbar.unsubscribe();
	}

	private mapSessionLimits(sessionLimit: MultiSessionLimitResponse): MultiSessionLimitTemplate {
		const mappedSessionLimit: MultiSessionLimitTemplate = this.getSessionLimitPeriodTranslation(
			this.getSessionLimitPeriod(sessionLimit)
		);
		mappedSessionLimit.activeFrom = sessionLimit.activeFrom;
		return mappedSessionLimit;
	}

	private getSessionLimitPeriod(sessionLimit: MultiSessionLimitResponse): MultiSessionLimit {
		const sessionLimitPeriod = pickBy(
			sessionLimit,
			(value, key: keyof MultiSessionLimitResponse) => value !== 0 && key !== "activeFrom"
		);
		return sessionLimitPeriod;
	}

	private getSessionLimitPeriodTranslation(sessionLimitPeriod: MultiSessionLimit): MultiSessionLimitTemplate {
		const key = Object.keys(sessionLimitPeriod)[0] as MultiSessionLimitPeriodTranslationsEnum;
		const period = Object.keys(MultiSessionLimitPeriodTranslationsEnum)
			.find(
				(x: keyof typeof MultiSessionLimitPeriodTranslationsEnum) =>
					MultiSessionLimitPeriodTranslationsEnum[x] === key
			)!
			.toLowerCase();
		return { activeFrom: "", limitValue: sessionLimitPeriod[key]!, limitPeriod: period };
	}

	private getMultiSessionLimitValues(
		sessionLimitAmount: number,
		sessionLimitPeriod: MultiSessionLimitPeriod
	): MultiSessionLimit {
		const period = multiSessionLimitPeriodToPeriodTranslation[sessionLimitPeriod];
		return { [period]: sessionLimitAmount };
	}
}
